import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import AWS from 'aws-sdk';
import { makeExcerpt } from '../utils/postContent';
import './Admin.css';

const Admin = () => {
  const { isAuthenticated, login, logout, isLoading } = useAuth();
  const [blogData, setBlogData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: 'Tips',
    featured: false
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedPDF, setSelectedPDF] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [uploadProgress, setUploadProgress] = useState('');
  const [loginData, setLoginData] = useState({
    username: '',
    password: ''
  });
  const [loginError, setLoginError] = useState('');

  const categories = ['Resorts', 'Dining', 'Parks', 'Tips', 'News', 'Sales'];

  // Configure AWS - using dedicated S3 uploader credentials
  AWS.config.update({
    region: 'ca-central-1',
    accessKeyId: 'AKIARU2W6AM7HUJ4SQIY',
    secretAccessKey: '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'
  });

  const s3 = new AWS.S3();

  // Helper function to upload an image directly to S3
  const uploadImageToS3 = async (file) => {
    const timestamp = Date.now();
    const ext = file.name.split('.').pop();
    const key = `blog-posts/images/${timestamp}.${ext}`;
    const uploadParams = {
      Bucket: 'chrystal-mousebound-site-prod-113462084414-ca-central-1',
      Key: key,
      Body: file,
      ContentType: file.type
    };
    const result = await s3.upload(uploadParams).promise();
    return result.Location;
  };

  // Helper function to update the manifest.json so Blog.js can find new posts
  const updateManifest = async (newSlug) => {
    const bucket = 'chrystal-mousebound-site-prod-113462084414-ca-central-1';
    const manifestKey = 'blog-posts/manifest.json';

    let slugs = [];
    try {
      const existing = await s3.getObject({ Bucket: bucket, Key: manifestKey }).promise();
      const parsed = JSON.parse(existing.Body.toString());
      slugs = parsed.posts || [];
    } catch (_) {
      // manifest doesn't exist yet — start fresh
    }

    if (!slugs.includes(newSlug)) {
      slugs.unshift(newSlug);
    }

    await s3.upload({
      Bucket: bucket,
      Key: manifestKey,
      Body: JSON.stringify({ posts: slugs }),
      ContentType: 'application/json'
    }).promise();
  };

  // Helper function to upload PDF directly to S3
  const uploadPDFToS3 = async (file) => {
    try {
      setUploadProgress('Uploading PDF to S3...');
      
      const timestamp = Date.now();
      const key = `blog-posts/${timestamp}/${file.name}`;
      
      // Actually upload the file to S3
      const uploadParams = {
        Bucket: 'chrystal-mousebound-site-prod-113462084414-ca-central-1',
        Key: key,
        Body: file,
        ContentType: 'application/pdf'
      };
      
      const result = await s3.upload(uploadParams).promise();
      setUploadProgress('PDF uploaded successfully!');
      
      return result.Location;
    } catch (error) {
      console.error('S3 upload error:', error);
      setUploadProgress('PDF upload failed - use manual upload command below');
      
      // Provide manual upload command as fallback
      const timestamp = Date.now();
      const key = `blog-posts/${timestamp}/${file.name}`;
      console.log(`Manual PDF Upload Command: aws s3 cp "${file.name}" s3://chrystal-mousebound-site-prod-113462084414-ca-central-1/${key} --profile mouse --acl public-read`);
      
      return `https://chrystal-mousebound-site-prod-113462084414-ca-central-1.s3.ca-central-1.amazonaws.com/${key}`;
    }
  };

  // Helper function to save blog post metadata to S3
  const saveBlogPostMetadata = async (postData) => {
    try {
      setUploadProgress('Uploading metadata to S3...');
      
      const metadataKey = `blog-posts/metadata/${postData.slug}.json`;
      
      // Actually upload metadata to S3
      const uploadParams = {
        Bucket: 'chrystal-mousebound-site-prod-113462084414-ca-central-1',
        Key: metadataKey,
        Body: JSON.stringify(postData, null, 2),
        ContentType: 'application/json'
      };
      
      await s3.upload(uploadParams).promise();
      
      // Also save to localStorage as backup
      const existingPosts = JSON.parse(localStorage.getItem('blogPosts') || '[]');
      existingPosts.push(postData);
      localStorage.setItem('blogPosts', JSON.stringify(existingPosts));
      
      setUploadProgress('Blog post metadata uploaded successfully!');
      
      return true;
    } catch (error) {
      console.error('Metadata save error:', error);
      setUploadProgress('Metadata upload failed - use manual upload command below');
      
      // Provide manual upload commands as fallback
      const metadataKey = `blog-posts/metadata/${postData.slug}.json`;
      console.log('=== MANUAL S3 UPLOAD COMMANDS ===');
      console.log('1. Upload metadata:');
      console.log(`aws s3 cp - s3://chrystal-mousebound-site-prod-113462084414-ca-central-1/${metadataKey} --profile mouse --acl public-read --content-type application/json << 'EOF'`);
      console.log(JSON.stringify(postData, null, 2));
      console.log('EOF');
      console.log('=== END COMMANDS ===');
      
      // Fallback to localStorage
      const existingPosts = JSON.parse(localStorage.getItem('blogPosts') || '[]');
      existingPosts.push(postData);
      localStorage.setItem('blogPosts', JSON.stringify(existingPosts));
      
      return false;
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    const result = await login(loginData.username, loginData.password);
    if (!result.success) {
      setLoginError(result.error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBlogData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLoginInputChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
    }
  };

  const handlePDFChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedPDF(file);
    } else if (file) {
      alert('Please select a PDF file.');
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');
    setUploadProgress('');

    try {
      // Generate slug from title
      const slug = blogData.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      // Upload image to S3
      let imageUrl = '';
      if (selectedImage) {
        try {
          setUploadProgress('Uploading image to S3...');
          imageUrl = await uploadImageToS3(selectedImage);
        } catch (imgError) {
          console.error('Image upload error:', imgError);
          imageUrl = '/images/1.png';
        }
      }

            // Handle PDF file - upload directly to S3
      let pdfUrl = '';
      let pdfFilename = '';
      if (selectedPDF) {
        pdfFilename = selectedPDF.name;
        const timestamp = Date.now();
        const s3Key = `blog-posts/${timestamp}/${pdfFilename}`;
        
        // Upload PDF directly to S3
        try {
          pdfUrl = await uploadPDFToS3(selectedPDF);
        } catch (pdfError) {
          console.error('PDF upload error:', pdfError);
          setUploadProgress('PDF upload failed - continuing with blog post creation');
          // Set a placeholder URL for now
          pdfUrl = `https://chrystal-mousebound-site-prod-113462084414-ca-central-1.s3.ca-central-1.amazonaws.com/${s3Key}`;
        }
      }

      // The article body belongs in `content`; `excerpt` is only the card summary.
      const excerpt = blogData.excerpt.trim()
        ? makeExcerpt(blogData.excerpt, 220)
        : makeExcerpt(blogData.content, 220);

      const postData = {
        title: blogData.title,
        excerpt: excerpt,
        content: blogData.content,
        category: blogData.category,
        featured: blogData.featured,
        image: imageUrl || '/images/1.png',
        slug: slug,
        date: new Date().toISOString(),
        pdf_url: pdfUrl,
        pdf_filename: pdfFilename
      };

      // Save metadata to S3
      await saveBlogPostMetadata(postData);

      // Update manifest so the blog page can discover this post
      setUploadProgress('Updating blog manifest...');
      await updateManifest(slug);

      setSubmitStatus('success');
      setBlogData({
        title: '',
        excerpt: '',
        content: '',
        category: 'Tips',
        featured: false
      });
      setSelectedImage(null);
      setSelectedPDF(null);
    } catch (error) {
      console.error('Upload error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading admin panel...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="admin-login">
        <div className="login-container">
          <h1>Admin Login</h1>
          <p>Please log in to access the admin panel</p>
          <form onSubmit={handleLogin} className="login-form">
            <div className="form-group">
              <label htmlFor="username">Email</label>
              <input
                type="email"
                id="username"
                name="username"
                value={loginData.username}
                onChange={handleLoginInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={loginData.password}
                onChange={handleLoginInputChange}
                required
              />
            </div>
            {loginError && (
              <div className="error-message">{loginError}</div>
            )}
            <button type="submit" className="btn-primary">
              Login
            </button>
          </form>
          <div className="login-hint">
            <p><strong>Demo Credentials:</strong></p>
                            <p>Email: chrystal.harris@playcationtravel.com</p>
            <p>Password: password123</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <section className="admin-hero">
        <div className="container">
          <div className="admin-header">
            <h1>Admin Panel</h1>
            <button onClick={logout} className="logout-btn">
              Logout
            </button>
          </div>
          <p>Manage your Disney travel blog content</p>
        </div>
      </section>

      <section className="admin-content">
        <div className="container">
          <div className="admin-grid">
            {/* Admin Navigation */}
            <div className="admin-nav">
              <Link to="/admin" className="nav-btn active">
                📝 Create Blog Post
              </Link>
              <Link to="/admin/blog-management" className="nav-btn">
                📊 Manage Blog Posts
              </Link>
            </div>
            
            {/* Blog Upload Form */}
            <div className="upload-form-container">
              <h2>Create New Blog Post</h2>
              <form onSubmit={handleSubmit} className="upload-form">
                <div className="form-group">
                  <label htmlFor="title">Title *</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={blogData.title}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="content">Article *</label>
                  <textarea
                    id="content"
                    name="content"
                    value={blogData.content}
                    onChange={handleInputChange}
                    rows="18"
                    placeholder="Paste the full article here — HTML (<h2>, <p>, <ul>) or Markdown both work."
                    required
                  />
                  <small className="form-hint">
                    The whole post goes here. HTML or Markdown — don't put it in the summary below.
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="excerpt">Card summary</label>
                  <textarea
                    id="excerpt"
                    name="excerpt"
                    value={blogData.excerpt}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="One or two sentences for the blog cards — leave blank to generate from the article."
                  />
                  <small className="form-hint">
                    Optional. Shown on the home page and blog cards only; generated from the
                    article's first paragraph if left blank.
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="category">Category *</label>
                  <select
                    id="category"
                    name="category"
                    value={blogData.category}
                    onChange={handleInputChange}
                    required
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="pdf">PDF Travel Guide *</label>
                  <input
                    type="file"
                    id="pdf"
                    accept=".pdf"
                    onChange={handlePDFChange}
                    required
                  />
                  <small>Upload your Disney travel guide, itinerary, or planning document as a PDF</small>
                  {selectedPDF && (
                    <div className="file-info">
                      <p>📄 Selected: {selectedPDF.name}</p>
                      <p>Size: {(selectedPDF.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="image">Featured Image</label>
                  <input
                    type="file"
                    id="image"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                  <small>Optional: Add a cover image for your travel guide</small>
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="featured"
                      checked={blogData.featured}
                      onChange={handleInputChange}
                    />
                    Featured Post
                  </label>
                </div>

                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Publishing...' : 'Publish Travel Guide'}
                </button>

                {uploadProgress && (
                  <div className="upload-progress">
                    <p>{uploadProgress}</p>
                  </div>
                )}

                {submitStatus && (
                  <div className={`submit-status ${submitStatus}`}>
                    {submitStatus === 'success' 
                      ? 'Travel guide published successfully! 🎉' 
                      : 'Error publishing travel guide. Please try again.'
                    }
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Admin; 